import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';
const RED = '#dc2626';
const AMBER = '#b45309';
const GOLD = '#c9a227';

// Shown when the user's Stripe billing needs attention. Three states:
//  - grace:   a renewal decline still inside its grace window, full access for now,
//             with a deadline to update the card before read-only kicks in.
//  - locked:  a decline whose grace has passed, the account is READ-ONLY (writes
//             return 402 and the app redirects them to Stripe).
//  - failing: a legacy/grandfathered past_due|canceled paid tier demoted to free.
// "Update Payment" opens the Stripe billing portal to fix the card on file.
export default function PaymentFailingBanner() {
  const [mode, setMode] = useState(null); // 'grace' | 'locked' | 'failing' | null
  const [graceUntil, setGraceUntil] = useState(null);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/stripe/subscription`)
      .then(res => {
        if (cancelled) return;
        const d = res.data || {};
        if (d.paymentLocked) setMode('locked');
        else if (d.graceUntil) { setMode('grace'); setGraceUntil(d.graceUntil); }
        else if (d.paymentFailing) setMode('failing');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const openPortal = async () => {
    setOpening(true);
    try {
      const res = await axios.post(`${API}/stripe/billing-portal`);
      if (res.data?.url) window.location.href = res.data.url;
      else setOpening(false);
    } catch { setOpening(false); }
  };

  if (!mode) return null;

  const bg = mode === 'grace' ? AMBER : RED;
  const graceDate = graceUntil ? new Date(graceUntil).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
  const headline = mode === 'grace' ? 'Your card was declined.'
    : mode === 'locked' ? 'Payment failed, your account is read-only.'
    : 'Your last payment failed.';
  const sub = mode === 'grace' ? `Update it${graceDate ? ` by ${graceDate}` : ''} to keep full access.`
    : mode === 'locked' ? 'Update your card to restore access.'
    : 'Paid features are paused until billing is updated.';

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const btnLabel = opening ? (isMobile ? '…' : 'Opening Stripe…') : 'Update Payment';

  if (isMobile) {
    return (
      <div style={{
        background: bg, color: 'white', padding: '8px 12px',
        fontFamily: "'Manrope', sans-serif", fontSize: 13,
        position: 'sticky', top: 0, zIndex: 51,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <strong style={{ color: GOLD }}>{headline}</strong>
          <span style={{ color: 'rgba(255,255,255,0.85)', marginLeft: 6 }}>{sub}</span>
        </span>
        <button onClick={openPortal} disabled={opening} style={{ background: GOLD, color: '#1a1a1a', border: 'none', padding: '6px 14px', borderRadius: 6, fontWeight: 800, fontSize: 12, cursor: opening ? 'wait' : 'pointer', flexShrink: 0 }}>
          {btnLabel}
        </button>
      </div>
    );
  }
  return (
    <div style={{
      background: bg, color: 'white', padding: '10px 18px',
      fontFamily: "'Manrope', sans-serif", fontSize: 13,
      borderBottom: `2px solid ${GOLD}`, position: 'sticky', top: 0, zIndex: 51,
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    }}>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ color: GOLD }}>{headline}</strong>
        <span style={{ color: 'rgba(255,255,255,0.9)' }}>{sub}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
        <button onClick={openPortal} disabled={opening} style={{ background: GOLD, color: '#1a1a1a', border: 'none', padding: '6px 16px', borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: opening ? 'wait' : 'pointer' }}>
          {opening ? 'Opening Stripe…' : 'Update Payment Method'}
        </button>
      </div>
    </div>
  );
}
